import {Injectable, TemplateRef} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {v4 as uuidv4} from 'uuid';
import {DefaultModalOptions, ModalInstance} from '../types/default-modal.types';
import {IDefaultModalService} from '@dcp/configurator';

@Injectable({
  providedIn: 'root'
})
export class DefaultModalService implements IDefaultModalService {
  private modals: ModalInstance[] = [];
  private modalsSubject = new BehaviorSubject<ModalInstance[]>([]);
  private topModalIdSubject = new BehaviorSubject<string | null>(null);
  private _onClose: Subject<string> = new Subject();

  public getActiveContentTemplate(id: string): Observable<TemplateRef<any>> {
    const modal = this.modals.find(modal => modal.id === id);
    return modal ? new BehaviorSubject(modal.contentTemplate).asObservable() : new BehaviorSubject(null).asObservable();
  }

  public getTopModalId(): Observable<string | null> {
    return this.topModalIdSubject.asObservable();
  }

  public getActiveFooterTemplate(id: string): Observable<TemplateRef<any>> {
    const modal = this.modals.find(modal => modal.id === id);
    return modal ? new BehaviorSubject(modal.footerTemplate).asObservable() : new BehaviorSubject(null).asObservable();
  }

  public getActiveTitle(id: string): Observable<string> {
    const modal = this.modals.find(modal => modal.id === id);
    return modal ? new BehaviorSubject(modal.title).asObservable() : new BehaviorSubject(null).asObservable();
  }

  public getActiveOptions(id: string): Observable<DefaultModalOptions> {
    const modal = this.modals.find(modal => modal.id === id);
    return modal ? new BehaviorSubject({
      ...modal.options,
      showHeader: modal.options?.showHeader ?? true
    }).asObservable() : new BehaviorSubject({showHeader: true}).asObservable();
  }

  openModal(contentTemplate: TemplateRef<any>, title: string, footerTemplate?: TemplateRef<any>, options?: DefaultModalOptions): string {
    const id = uuidv4();

    if (!contentTemplate || !title) {
      this.closeModal(id);
      return;
    }

    const newModal: ModalInstance = {id, contentTemplate, title, footerTemplate, options};
    this.modals.push(newModal);
    this.modalsSubject.next([...this.modals]);
    this.topModalIdSubject.next(id);
    return id;
  }

  getModals(): Observable<ModalInstance[]> {
    return this.modalsSubject.asObservable();
  }

  closeModal(id: string): void {
    this.modals = this.modals.filter(modal => modal.id !== id);
    this.modalsSubject.next([...this.modals]);
    const newTopModal = this.modals.length > 0 ? this.modals[this.modals.length - 1].id : null;
    this.topModalIdSubject.next(newTopModal);
    this._onClose.next(id);
  }

  public onClose(): Observable<string> {
    return this._onClose.asObservable();
  }
}
